import { useCallback, useEffect, useMemo } from "react";

import { SESSION_STORAGE_KEYS } from "@dw/constants";
import { IDanceSchool } from "@dw/models/danceSchool";
import { Dayjs, dayjs } from "@dw/dayjs";

import { useSessionStorage } from "./useSessionStorage";
import { useAuth } from ".";

interface IActiveEnrolmentYearSettings {
  activeEnrolmentYear: number;
}

export const useActiveEnrolmentYear = () => {

  const { danceSchool } = useAuth();
  const { value: activeEnrolmentYearSettings, initialized: activeEnrolmentYearSettingsInitialized, updateItem: setActiveEnrolmentYearSettings } = useSessionStorage<IActiveEnrolmentYearSettings>(SESSION_STORAGE_KEYS.ActiveEnrolmentYear);

  const { isLatestEnrolmentYearActive, latestEnrolmentYear, activeEnrolmentYear, activeEnrolmentSettings } = useMemo(() => {
    const currentEnrolmentYear = getDanceSchoolCurrentEnrolmentYear(danceSchool);
    const isLatestEnrolmentYearActive = activeEnrolmentYearSettings?.activeEnrolmentYear === currentEnrolmentYear;

    const activeEnrolmentYear = activeEnrolmentYearSettings?.activeEnrolmentYear ?? currentEnrolmentYear;
    const activeEnrolmentSettings = (danceSchool?.annualSettings ?? [])
      .find(x => x.year === activeEnrolmentYear);

    return {
      isLatestEnrolmentYearActive,
      activeEnrolmentYear,
      activeEnrolmentSettings,
      latestEnrolmentYear: currentEnrolmentYear
    }
  }, [danceSchool, activeEnrolmentYearSettings]);

  const setActiveEnrolmentYear = useCallback((year: number) => {
    setActiveEnrolmentYearSettings({
      activeEnrolmentYear: year
    });
  }, [setActiveEnrolmentYearSettings]);

  const getEnrolmentTermDetailsForDate = useCallback((date: Dayjs, exactTerm?: boolean) => {
    return (activeEnrolmentSettings?.termDates ?? [])
      .map((td, index) => ({ termNumber: index + 1, termDates: td }))
      .filter(x => x.termDates.endDate.isAfter(date, 'date') && (!exactTerm || x.termDates.startDate.isSameOrBefore(date, 'date')))
      .sort((a, b) => a.termDates.startDate.valueOf() - b.termDates.startDate.valueOf())[0] ?? null;
  }, [activeEnrolmentSettings]);

  useEffect(() => {
    if (!danceSchool || !activeEnrolmentYearSettingsInitialized || activeEnrolmentYearSettings?.activeEnrolmentYear) {
        return;
    }

    const currentEnrolmentYear = getDanceSchoolCurrentEnrolmentYear(danceSchool);
    setActiveEnrolmentYearSettings({ activeEnrolmentYear: currentEnrolmentYear });
  }, [danceSchool, activeEnrolmentYearSettingsInitialized, activeEnrolmentYearSettings]);

  return {
    isLatestEnrolmentYearActive,
    activeEnrolmentYear,
    activeEnrolmentSettings,
    latestEnrolmentYear,
    setActiveEnrolmentYear,
    getEnrolmentTermDetailsForDate
  }
}

const getDanceSchoolCurrentEnrolmentYear = (danceSchool?: IDanceSchool): number => {
  const annualSettings = (danceSchool?.annualSettings ?? []);
  const latestAnnualSettings = [...annualSettings].sort((a, b) => b.year - a.year)[0];
  const latestAnnualSettingsLastTermEndDate = [...(latestAnnualSettings?.termDates ?? [])]
    .sort((a, b) => b.endDate.valueOf() - a.endDate.valueOf())[0]?.endDate;

  let currentEnrolmentYear = latestAnnualSettings?.year ?? dayjs().year(); 
  if (latestAnnualSettingsLastTermEndDate && dayjs().isAfter(latestAnnualSettingsLastTermEndDate, 'date')) {
    currentEnrolmentYear++;
  }

  return currentEnrolmentYear;
}